import React, { useState, useRef, useEffect } from 'react';
import './styles.css';
import downArrow from '../../images/arrowDown.svg';
import download from '../../images/Download.svg';
import { usePlanFeatures } from '../../contexts/PlanFeaturesContext';
import ExternalLinkModal from '../externalLinkModal/externalLinkModal';

const StateOptions = ({ onCsedProviderClick }) => {
    // Get state options data from PlanFeaturesContext
    const { getStateOptionsData, loading } = usePlanFeatures();
    const stateOptionsData = getStateOptionsData();

    const [expandedStates, setExpandedStates] = useState({});

    // "You are now leaving..." confirmation modal for any external <a> link that comes
    // through inside a state option's dangerouslySetInnerHTML content (option.contentHTML)
    // — same pattern/component used for providerDetailsType2.jsx's external links.
    const [externalLinkModalOpen, setExternalLinkModalOpen] = useState(false);
    const [pendingExternalUrl, setPendingExternalUrl] = useState(null);

    // Each accordion's content is rendered via dangerouslySetInnerHTML (option.contentHTML),
    // so any links it contains can't be given a React tabIndex prop directly. The section
    // collapses via CSS (max-height: 0 + aria-hidden) rather than display:none — which hides
    // it visually/from AT but does NOT remove its descendant links from the native keyboard
    // tab order. That's what let a collapsed section's hidden links stay tabbable. This ref
    // map + effect imperatively sets tabIndex={-1} on every focusable element inside each
    // collapsed content block (and restores tabIndex=0 when expanded), keeping them out of
    // the tab order while hidden.
    //
    // IMPORTANT: this effect must also re-run whenever stateOptionsData itself changes, not
    // just expandedStates. planFeatures (and therefore stateOptionsData) loads ASYNCHRONOUSLY
    // — on first mount it's `[]` (nothing to tab-index), then a later render swaps in the real
    // accordion content once the plan-features fetch resolves. That later render mounts brand
    // new <a>/<button> elements inside each (still-collapsed) content block for the very first
    // time, with no tabindex attribute of their own — natively focusable and in the tab order
    // by default. With only `[expandedStates]` as a dependency, this effect had already run
    // once (against the empty initial data) and had no reason to run again once the real
    // content arrived, since expandedStates didn't change — so those newly-mounted hidden
    // links never got tabIndex=-1 applied at all, and stayed in the tab order while visually
    // hidden. This was reported by accessibility QA (screen recording, mac/Chrome): "hidden
    // links in the expandable content are still in tab order" on the "More provider info"
    // section. Depending on stateOptionsData too ensures this re-syncs every time the
    // accordions' actual DOM content changes, not just when a user expands/collapses one.
    const contentRefs = useRef({});

    useEffect(() => {
        Object.keys(contentRefs.current).forEach((optionId) => {
            const container = contentRefs.current[optionId];
            if (!container) return;
            const isExpanded = expandedStates[optionId] || false;
            const focusableElements = container.querySelectorAll(
                'a, button, input, select, textarea, [tabindex]'
            );
            focusableElements.forEach((el) => {
                el.setAttribute('tabindex', isExpanded ? '0' : '-1');
            });

            // Reported: "links inside a collapsed accordion still open in the same tab,
            // leaving the Find care page" — the raw CMS-authored contentHTML doesn't
            // reliably mark every external link with the `external-link` class that
            // handleContentClick below looks for (that class-based interception was only
            // ever a best-effort convenience, not guaranteed present on every anchor the
            // content authors write). Belt-and-suspenders fix: for every http(s) anchor in
            // this content block (regardless of class), force target="_blank" and
            // rel="noopener noreferrer" directly on the DOM node. That way even an anchor
            // handleContentClick doesn't recognize as "external" (missing the class) still
            // opens in a new tab instead of navigating the user away from Find Care, and
            // never grants the new tab a `window.opener` reference back to this page.
            container.querySelectorAll('a[href^="http:"], a[href^="https:"]').forEach((a) => {
                a.setAttribute('target', '_blank');
                a.setAttribute('rel', 'noopener noreferrer');
            });
        });
    }, [expandedStates, stateOptionsData]);

    const toggleExpanded = (optionId) => {
        setExpandedStates(prev => ({
            ...prev,
            [optionId]: !prev[optionId]
        }));
    };

    // Since option.contentHTML is injected via dangerouslySetInnerHTML, any <a> tags inside
    // it aren't real React elements we can attach onClick handlers to directly. Instead,
    // delegate: intercept the click at the container level, and if it targets (or is inside)
    // an external (http/https) <a>, show the "you are now leaving" confirmation modal before
    // opening it in a new tab — same pattern as providerDetailsType2.jsx.
    //
    // NOTE: this used to only match `a.external-link` — but the raw CMS-authored
    // contentHTML doesn't reliably apply that class to every external link (content
    // authors write plain <a href="https://..."> without it), so links missing the class
    // fell through untouched and navigated the current tab away from Find Care entirely
    // (reported bug). Matching on the href scheme instead of a class name catches every
    // external link regardless of how the source content happens to be authored.
    const handleContentClick = (e) => {
        // CSED (Children with Serious Emotional Disorder Waiver) provider map link —
        // CMS-authored contentHTML marks this link with the `csedProv` class instead of a
        // real URL. Clicking it should switch the whole page into a dedicated
        // "CSED provider map" view (header/footer stay, everything else hides) rather than
        // navigating anywhere, so this must be checked BEFORE the generic external-link
        // handling below (which would otherwise ignore it anyway, since it has no http(s)
        // href to match).
        const csedAnchor = e.target.closest('.csedProv');
        if (csedAnchor) {
            e.preventDefault();
            onCsedProviderClick?.();
            return;
        }

        const anchor = e.target.closest('a[href^="http:"], a[href^="https:"]');
        if (!anchor) return;

        // Only trigger the "you are now leaving" modal when the anchor is explicitly
        // marked as external via its className. Otherwise, let the anchor behave
        // normally (e.g. default browser navigation/new tab per its own attributes).
        if (!anchor.className || !anchor.className.split(/\s+/).includes('external')) {
            return;
        }

        const href = anchor.getAttribute('href');
        if (!href || !/^https?:\/\//i.test(href)) return;
        e.preventDefault();
        setPendingExternalUrl(href);
        setExternalLinkModalOpen(true);
    };

    const handleExternalLinkConfirm = () => {
        if (pendingExternalUrl) {
            window.open(pendingExternalUrl, '_blank', 'noopener,noreferrer');
        }
        setExternalLinkModalOpen(false);
        setPendingExternalUrl(null);
    };

    const handleExternalLinkCancel = () => {
        setExternalLinkModalOpen(false);
        setPendingExternalUrl(null);
    };

    const createStateOption = (option, index) => {
        const isExpanded = expandedStates[option.id] || false;
        const isLastItem = index === stateOptionsData.length - 1;
        const resolvedHTML = option.contentHTML?.replace(/\$\{download\}/g, download);

        return (
            <div key={option.id}>
                <button 
                    type="button"
                    className={`stateOptions ${!isLastItem ? 'mb30' : ''} ${isExpanded ? 'expanded' : ''}`}
                    aria-expanded={isExpanded}
                    aria-controls={`${option.id}-content`}
                    onClick={() => toggleExpanded(option.id)}
                >
                    <span>{option.label}</span>
                    <img 
                        src={downArrow} 
                        className={`arrowDown ${isExpanded ? 'rotated' : ''}`} 
                        alt="" 
                        aria-hidden="true" 
                    />
                </button>
                
                {/* Expandable content area */}
                <div 
                    id={`${option.id}-content`}
                    ref={(el) => { contentRefs.current[option.id] = el; }}
                    className={`expandable-content provInfo ${isExpanded ? 'expanded' : ''}`}
                    aria-hidden={!isExpanded}
                >
                    <div className="content" onClick={handleContentClick}>
                        {option.content 
                            ? option.content 
                            : <div dangerouslySetInnerHTML={{ __html: resolvedHTML }} />
                        }
                    </div>
                </div>
            </div>
        );
    };

    // Don't render anything if there's no data
    if (!loading && stateOptionsData.length === 0) {
        return null;
    }

    return (
        <div id="stateOptionsDiv" className="mb30">
            <h3 className="mb30">More provider info</h3>
            {stateOptionsData.map(createStateOption)}

            <ExternalLinkModal
                isOpen={externalLinkModalOpen}
                onConfirm={handleExternalLinkConfirm}
                onCancel={handleExternalLinkCancel}
                url={pendingExternalUrl}
            />
        </div>
    );
};

export default StateOptions;
